/**
 * YouTube, through the YouTube Data API v3. Video only: the upload is the post.
 *
 * Read from the official documentation:
 *  - https://developers.google.com/identity/protocols/oauth2/web-server
 *      authorize / token / revoke addresses, access_type=offline,
 *      prompt=consent, include_granted_scopes, space-separated scopes.
 *  - https://developers.google.com/youtube/v3/guides/using_resumable_upload_protocol
 *      POST ...upload/youtube/v3/videos?uploadType=resumable&part=snippet,status
 *      with X-Upload-Content-Length / X-Upload-Content-Type, the upload address
 *      in the Location header, the whole file PUT there, the video resource
 *      (201) as the answer.
 *  - https://developers.google.com/youtube/v3/docs/videos/insert
 *  - https://developers.google.com/youtube/v3/docs/videos/update  (PUT, 50 units)
 *  - https://developers.google.com/youtube/v3/docs/videos/delete  (DELETE ?id=, 204, 50 units)
 *  - https://developers.google.com/youtube/v3/docs/channels/list  (mine=true, 1 unit)
 *  - https://developers.google.com/youtube/v3/docs/search/list    (forMine=true needs type=video)
 *  - https://developers.google.com/youtube/v3/docs/videos         (title <= 100 chars, description <= 5000 bytes, no < or >)
 *
 * Scope: one, `youtube.force-ssl`. Every call this definition makes (insert,
 * update, delete, channels.list mine, search.list forMine) accepts it, and one
 * scope cannot be half-granted on Google's granular consent screen the way two
 * could (a person unticking the one that lets us delete).
 *
 * Choices worth knowing about:
 *  - PRIVACY IS FIXED AT `private`. The settings schema has no default value, so
 *    a `privacy` setting left empty would drop the field and leave the choice to
 *    YouTube, which may publish publicly. Every upload from an API project that
 *    has not passed Google's compliance audit is locked to private anyway
 *    ("videos.insert from unverified API projects created after 28 July 2020
 *    will be restricted to private viewing mode"). Once the project is audited,
 *    change `private` below to `unlisted` or `public` on the networks screen.
 *  - EMPTY TITLE. YouTube rejects a video with no title and the template
 *    language has no fallback, so `{{title}}` (the text's first line, at most
 *    100 characters) is sent as it is: a video posted with no text is refused by
 *    YouTube with its own message ("invalid or empty video title") rather than
 *    given an invented title. The post text must therefore not be empty.
 *  - `maxTextLength` is 2500, not 5000: the limit is 5000 BYTES and Arabic takes
 *    two bytes a letter. `<` and `>` are forbidden by YouTube and are not
 *    stripped here (there is no such text transform).
 *  - Editing re-sends the snippet. videos.update needs title and categoryId and
 *    DELETES any snippet field it is not given (tags, for one), so an edit made
 *    here overwrites tags and category changed later in YouTube Studio.
 *  - Listing uses search.list (forMine) because the uploads playlist id cannot
 *    be derived with the template language; the documentation lists it as a
 *    separate 100-calls-a-day bucket. At most 50 results, newest first.
 *
 * Not verified against the live API (needs a real Google project):
 *  - quota: the quota page lists videos.insert and search.list as "100 per day,
 *    1 per call" in their own buckets; older documentation said 1600 units for
 *    an upload. Confirm in the Google Cloud console.
 *  - an unverified OAuth consent screen in "Testing" status expires refresh
 *    tokens after 7 days (not stated on the pages above; known from Google's
 *    OAuth policy), so the account has to be reconnected weekly until the
 *    consent screen is published.
 *  - the revoke call sends client_id and client_secret along with `token`; Google
 *    documents only `token`.
 *  - `selfDeclaredMadeForKids` is not sent, so the channel's default applies.
 */
export const YOUTUBE_PRESET = {
    key: 'YOUTUBE',
    name: 'YouTube',
    verifiedLive: false,
    contract: {
        version: 1,
        allowedHosts: ['www.googleapis.com', 'oauth2.googleapis.com', 'accounts.google.com'],
        auth: {
            type: 'oauth2',
            authorizeUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
            tokenUrl: 'https://oauth2.googleapis.com/token',
            revokeUrl: 'https://oauth2.googleapis.com/revoke',
            scopes: ['https://www.googleapis.com/auth/youtube.force-ssl'],
            scopeSeparator: ' ',
            pkce: false,
            clientSecretIn: 'body',
            // offline + consent: Google returns a refresh token only on the first
            // grant unless the person is asked again.
            extraAuthorizeParams: {
                access_type: 'offline',
                prompt: 'consent',
                include_granted_scopes: 'true',
            },
            refresh: true,
        },
        tokenPlacement: { in: 'bearer' },
        settings: [
            {
                name: 'privacy',
                label: 'Who can watch new videos (private until Google has audited the API project)',
                type: 'string',
                default: 'private',
                options: ['private', 'unlisted', 'public'],
            },
        ],
        identity: {
            request: {
                method: 'GET',
                url: 'https://www.googleapis.com/youtube/v3/channels?part=snippet&mine=true',
            },
            idPath: 'items[0].id',
            namePath: 'items[0].snippet.title',
            accountId: '{{entry.id}}',
            kind: 'ACCOUNT',
        },
        publish: {
            kinds: ['VIDEO'],
            // Never called: a video's upload is the post (mediaIsPost). The schema wants one.
            request: {
                method: 'POST',
                url: 'https://www.googleapis.com/youtube/v3/videos?part=snippet,status',
                json: {},
            },
            mediaIsPost: ['VIDEO'],
            resultId: { from: 'body', path: 'id' },
            maxTextLength: 2500,
            // YouTube refuses an empty title; a video posted without text gets this one.
            titleFallback: 'Video',
        },
        media: {
            video: {
                type: 'resumable',
                init: {
                    method: 'POST',
                    url: 'https://www.googleapis.com/upload/youtube/v3/videos?uploadType=resumable&part=snippet,status',
                    headers: {
                        'X-Upload-Content-Length': '{{fileSize}}',
                        'X-Upload-Content-Type': '{{mimeType}}',
                    },
                    json: {
                        snippet: {
                            title: '{{title}}',
                            description: '{{text}}',
                            // 22 = People & Blogs, the category every channel has.
                            categoryId: '22',
                        },
                        status: { privacyStatus: '{{setting.privacy}}' },
                    },
                },
                locationHeader: 'location',
                uploadMethod: 'PUT',
                mediaIdPath: 'id',
            },
        },
        posts: {
            list: {
                request: {
                    method: 'GET',
                    url: 'https://www.googleapis.com/youtube/v3/search?part=snippet&forMine=true&type=video&order=date&maxResults={{count}}',
                },
                listPath: 'items',
                idPath: 'id.videoId',
                textPath: 'snippet.title',
                createdPath: 'snippet.publishedAt',
                createdFormat: 'iso',
            },
            edit: {
                method: 'PUT',
                url: 'https://www.googleapis.com/youtube/v3/videos?part=snippet',
                json: {
                    id: '{{postId}}',
                    snippet: { title: '{{title}}', description: '{{text}}', categoryId: '22' },
                },
            },
            delete: {
                method: 'DELETE',
                url: 'https://www.googleapis.com/youtube/v3/videos?id={{postId}}',
            },
        },
        permalink: 'https://www.youtube.com/watch?v={{postId}}',
        errorMessagePaths: ['error.message', 'error_description'],
    },
};
//# sourceMappingURL=youtube.js.map